<script setup lang="ts">
import { inject, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useZoppm } from '/@src/composable/useZoppm'
import { formatDateString } from '/@src/utils/format'

const emptyResponse = inject('emptyResponse')
const { t } = useI18n()
const isReadOnly = inject<boolean>('isReadOnly')
const zoppm = !isReadOnly ? useZoppm() : null
const konecniUzivateliaVyhod = ref([])

onMounted(async () => {
  if (!isReadOnly) {
    konecniUzivateliaVyhod.value = await zoppm
      ?.getKonecniUzivateliaVyhod()
      .then((r) => r?.osoby)
  }
})
</script>

<template>
  <div class="idsk-crossroad-line mb-8 mt-8"></div>
  <IDSKHeading as="h2" variant="l">
    {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodHeading') }}
  </IDSKHeading>
  <IDSKParagraph>
    {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodDescription') }}
  </IDSKParagraph>

  <template v-if="!isReadOnly">
    <div v-for="osoba in konecniUzivateliaVyhod" :key="osoba">
      <IDSKParagraph class="govuk-!-font-weight-bold mb-0">
        {{ osoba?.osobaRef?.menoUplne }}
      </IDSKParagraph>
      <IDSKParagraph class="mb-6">
        <Address :data="osoba?.osobaRef?.adresaUplna" />
      </IDSKParagraph>
      <IDSKParagraph v-if="osoba?.osobaRef?.datumNarodenia" class="mb-0">
        {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodNarodenie') }}
        {{ formatDateString(osoba?.osobaRef?.datumNarodenia) }}
      </IDSKParagraph>
      <IDSKParagraph class="mb-4"> </IDSKParagraph>
    </div>

    <ValidationWrapper
      v-if="konecniUzivateliaVyhod?.length > 0"
      v-slot="{ 'onUpdate:modelValue': update, modelValue, errorMessage, ...slotProps }"
      name="ziadatel.organizacia.konecniUzivateliaVyhod.udajeOvereneZiadatelom"
    >
      <div
        :class="[
          'govuk-form-group',
          'mb-0',
          {
            ['govuk-form-group--error']: errorMessage,
          },
        ]"
      >
        <IDSKFieldCaption
          v-if="errorMessage"
          :error-message="errorMessage"
          variant="legend"
        />
        <IDSKCheckbox
          v-bind="slotProps"
          id="konecniUzivateliaVyhod-udajeOvereneZiadatelom"
          :key="modelValue"
          :checked="modelValue"
          @is-checked="(val) => update(val)"
        >
          <template #default>
            <IDSKParagraph class="mb-0">
              {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodCheckBoxTitle') }}
            </IDSKParagraph>
          </template>
        </IDSKCheckbox>

        <div class="govuk-inset-text mb-8 mt-0">
          <IDSKParagraph>
            {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodCheckBoxDescription') }}
            <IDSKLink href="/app/subjekt/organizacia/osoby" class="mb-6" role="button">
              {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodDoplnitUdaje') }}
            </IDSKLink>
          </IDSKParagraph>
        </div>
      </div>
    </ValidationWrapper>
    <div v-else>
      <div v-if="!isReadOnly">
        <div class="error-message">
          <p>
            {{ t('zoppm.step7.pravnickaOsoba.statutarKUVError') }} <br />
            {{ t('zoppm.step7.pravnickaOsoba.statutarKUVErrorDesc') }}
          </p>
        </div>
        <div class="error-border">
          <div class="govuk-inset-text mt-0">
            <IDSKParagraph>
              {{
                t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodCheckBoxDescription')
              }}
              <IDSKLink href="/app/subjekt/organizacia/osoby" class="mb-6" role="button">
                {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodDoplnitUdaje') }}
              </IDSKLink>
            </IDSKParagraph>
          </div>
        </div>
        <ValidationWrapper
          v-slot="{ ...slotProps }"
          name="ziadatel.organizacia.konecniUzivateliaVyhod.udajeOvereneZiadatelom"
        >
          <div
            v-bind="slotProps"
            id="konecniUzivateliaVyhod-udajeOvereneZiadatelom"
          ></div>
        </ValidationWrapper>
      </div>
    </div>
  </template>

  <template v-else>
    <div
      v-for="osoba in emptyResponse.ziadatel.organizacia.konecniUzivateliaVyhod.osoby"
      :key="osoba"
    >
      <IDSKParagraph class="govuk-!-font-weight-bold mb-0">
        {{ t('zoppm.step7.fyzickaOsoba.menoAPriezviskoVzorova') }}
      </IDSKParagraph>
      <IDSKParagraph class="mb-6">{{
        t('zoppm.step7.pravnickaOsoba.adresaVzorova')
      }}</IDSKParagraph>
      <IDSKParagraph class="mb-0">
        {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodNarodenie') }}
        {{ t('zoppm.step7.fyzickaOsoba.datumNarodeniaVzorova') }}
      </IDSKParagraph>
      <IDSKParagraph class="mb-4"> </IDSKParagraph>
    </div>
    <div class="govuk-inset-text mb-8">
      <IDSKCheckbox id="konecniUzivateliaVyhod-udajeOvereneZiadatelom">
        <template #default>
          <IDSKParagraph>
            {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodCheckBoxTitle') }}
          </IDSKParagraph>
        </template>
      </IDSKCheckbox>
      <IDSKParagraph>
        {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodCheckBoxDescription') }}
      </IDSKParagraph>
      <IDSKLink class="mb-6" href="#" no-visited-state>
        {{ t('zoppm.step7.pravnickaOsoba.konecneUzivatelVyhodDoplnitUdaje') }}
      </IDSKLink>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.error-border {
  border-left: 5px solid #d0190f;
  padding-left: 1em;
}

.error-message {
  color: #d0190f;
  margin-bottom: 2em;
  font-weight: 600;
}
</style>
